/**
 * Data Explorer — spreadsheet-style browsing of submitted form data.
 *
 * Two bases: single-form (auto views) and record-centric (multi-form joins).
 * Shape with the column picker, filter chips, instance/per-form strategy, sort,
 * column reorder, group-by + summaries, charts, and export — saved as private or
 * (admins) project-shared views.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Save, Database, Layers, Plus, BarChart3, Download, Search, Sparkles,
  Maximize2, Minimize2, PanelRightClose, PanelRightOpen,
} from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/shared/empty-state'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { isServerQueryable, type FormCatalog } from '../lib/column-catalog'
import { buildAutoViewSpec } from '../lib/auto-view'
import {
  resolveTable, resolveRecordTable, type ResolvedColumn, type ResolvedRow, type ResolvedTable,
} from '../lib/spec-resolver'
import { formatCellValue } from '../lib/value-format'
import { groupRows } from '../lib/group-by'
import { type ChartBuilderConfig, DEFAULT_CHART_CONFIG } from '../lib/chart-spec'
import { tableToMatrix, downloadCsv, downloadXlsx } from '../lib/export'
import {
  useExplorerFormsQuery, useDataViewsQuery, useFormCatalogQuery, useFormCatalogsQuery,
  useFormViewQuery, useRecordViewQuery,
  useCreateDataViewMutation, useDeleteDataViewMutation,
} from '../api/use-explorer'
import { useAiSavedViewsQuery, useDeleteSavedViewMutation } from '../api/use-ai-explore'
import type { AiSavedView } from '../api/ai-storage'
import { queryFormView, queryRecordView } from '../api/explorer-api'
import { ViewSidebar, type ActiveSelection } from '../components/view-sidebar'
import { ColumnPicker } from '../components/column-picker'
import { RecordColumnPicker } from '../components/record-column-picker'
import { FilterBar } from '../components/filter-bar'
import { RecordFilterBar } from '../components/record-filter-bar'
import { InstanceStrategyPicker } from '../components/instance-strategy-picker'
import { ColumnReorder } from '../components/column-reorder'
import { ExplorerGrid } from '../components/explorer-grid'
import { GroupedTable } from '../components/grouped-table'
import { ExportDialog, type ExportOptions } from '../components/export-dialog'
import { ArrayDetailDialog } from '../components/array-detail-dialog'
import { SaveViewDialog, type SaveViewPayload } from '../components/save-view-dialog'
import { ViewBuilderDialog, type ViewBuilderPayload } from '../components/view-builder-dialog'
import {
  META_COLUMNS, type DataViewSpec, type DataViewRow, type ViewColumn, type FormInstanceStrategy,
} from '../types/data-view-spec'

// Vega (~1 MB) loads only when the chart builder opens.
const ChartBuilderDialog = lazy(() => import('../components/chart-builder-dialog').then((m) => ({ default: m.ChartBuilderDialog })))
// AI panels — lazy so Vega/chat stay out of the main bundle.
const AiWorkspace = lazy(() => import('../components/ai-workspace').then((m) => ({ default: m.AiWorkspace })))
const AiViewSurface = lazy(() => import('../components/ai-view-surface').then((m) => ({ default: m.AiViewSurface })))

const PAGE_SIZE = 50
const ALL_PAGE = 500
const ALL_CAP = 50_000

const META_SORT: Record<string, string> = {
  display_id: 'display_id', record_status: 'status',
  record_created_at: 'created_at', record_updated_at: 'updated_at', instance_index: 'instance_index',
}

function buildSortMap(columns: ResolvedColumn[], layout: 'long' | 'grouped' | 'record'): Record<string, string> {
  const map: Record<string, string> = {}
  for (const col of columns) {
    if (col.kind === 'meta' && col.metaKey && META_SORT[col.metaKey]) {
      if (layout === 'record' && col.metaKey === 'instance_index') continue
      map[col.id] = META_SORT[col.metaKey]
    } else if (col.kind === 'field' && !col.instanceIndex && layout === 'long' && col.catalogField && isServerQueryable(col.catalogField)) {
      map[col.id] = col.catalogField.fieldKey
    }
  }
  return map
}

export function DataExplorerPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const { canManageForms: canManage } = useProjectRole()

  const formsQ = useExplorerFormsQuery(projectId)
  const viewsQ = useDataViewsQuery(projectId)
  const views = viewsQ.data ?? []

  const [active, setActive] = useState<ActiveSelection | null>(null)
  const [spec, setSpec] = useState<DataViewSpec | null>(null)
  const [page, setPage] = useState(0)
  const [saveOpen, setSaveOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [chartOpen, setChartOpen] = useState(false)
  const [groupColId, setGroupColId] = useState('')
  const [chartConfig, setChartConfig] = useState<ChartBuilderConfig>(DEFAULT_CHART_CONFIG)
  const [arrayDialog, setArrayDialog] = useState<{ title: string; field?: FormCatalog['fields'][number]; entries: unknown[] } | null>(null)
  const [allRows, setAllRows] = useState<ResolvedRow[] | null>(null)
  const [allLoading, setAllLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [builderOpen, setBuilderOpen] = useState(false)
  const aiViewsQ = useAiSavedViewsQuery(projectId)
  const activeAiView: AiSavedView | undefined =
    active?.type === 'ai' ? aiViewsQ.data?.find((v) => v.id === active.viewId) : undefined
  const deleteAiViewMut = useDeleteSavedViewMutation(projectId!)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 350)
  const [sidebarW, setSidebarW] = useState<number>(() => {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('explorer-sidebar-w') : null
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) && n >= 220 && n <= 520 ? n : 320
  })
  const sidebarDrag = useRef<{ startX: number; startW: number; rtl: boolean } | null>(null)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    return typeof window !== 'undefined' && window.localStorage.getItem('explorer-sidebar-collapsed') === '1'
  })
  const [fullscreen, setFullscreen] = useState(false)
  const builtKey = useRef<string | null>(null)
  const allKey = useRef<string | null>(null)

  // Sidebar resize
  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = sidebarDrag.current
      if (!d) return
      const delta = d.rtl ? d.startX - e.clientX : e.clientX - d.startX
      setSidebarW(Math.max(220, Math.min(520, d.startW + delta)))
    }
    const onUp = () => {
      if (sidebarDrag.current) { try { window.localStorage.setItem('explorer-sidebar-w', String(sidebarW)) } catch { /* ignore */ } }
      sidebarDrag.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp) }
  }, [sidebarW])

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((c) => {
      const next = !c
      try { window.localStorage.setItem('explorer-sidebar-collapsed', next ? '1' : '0') } catch { /* ignore */ }
      return next
    })
  }, [])

  // ESC exits table fullscreen.
  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const isRecord = spec?.base.kind === 'record'
  const activeView: DataViewRow | undefined =
    active?.type === 'view' ? views.find((v) => v.id === active.viewId) : undefined

  // Auto-select first form on load.
  useEffect(() => {
    if (!active && formsQ.data && formsQ.data.length > 0) {
      const first = formsQ.data[0].forms[0]
      if (first) setActive({ type: 'form', formId: first.id })
    }
  }, [active, formsQ.data])

  // Single-form catalog
  const singleFormId = useMemo(() => {
    if (active?.type === 'form') return active.formId
    if (activeView?.spec.base.kind === 'form') return activeView.spec.base.formId
    return undefined
  }, [active, activeView])
  const catalogQ = useFormCatalogQuery(isRecord ? undefined : singleFormId)
  const singleCatalog = catalogQ.data?.catalog

  // Record-centric catalogs
  const recordFormIds = useMemo(
    () => (isRecord && spec ? [...new Set(spec.columns.filter((c) => c.source === 'field' && c.formId).map((c) => c.formId!))] : []),
    [isRecord, spec],
  )
  const catalogsQ = useFormCatalogsQuery(recordFormIds)
  const catalogs = catalogsQ.data?.catalogs ?? {}
  const titles = catalogsQ.data?.titles ?? {}

  // Build working spec when source changes.
  const sourceKey = active
    ? active.type === 'form' ? `form:${active.formId}`
    : active.type === 'ai' ? `ai:${active.viewId}`
    : `view:${active.viewId}`
    : null

  useEffect(() => {
    if (!active) { setSpec(null); builtKey.current = null; return }
    if (builtKey.current === sourceKey) return
    if (active.type === 'view' && activeView) {
      setSpec(structuredClone(activeView.spec)); setPage(0); setGroupColId(''); builtKey.current = sourceKey
    } else if (active.type === 'form' && catalogQ.data?.catalog.formId === active.formId) {
      setSpec(buildAutoViewSpec(active.formId, catalogQ.data.catalog, catalogQ.data.instanceType))
      setPage(0); setGroupColId(''); builtKey.current = sourceKey
    }
  }, [active, activeView, sourceKey, catalogQ.data])

  // Paged data
  const formDataQ = useFormViewQuery(isRecord ? null : spec, page, PAGE_SIZE)
  const recordDataQ = useRecordViewQuery(isRecord ? spec : null, projectId, page, PAGE_SIZE)

  const ready = isRecord ? (recordFormIds.length === 0 || catalogsQ.isSuccess) : !!singleCatalog
  const dataQ = isRecord ? recordDataQ : formDataQ

  const resolveCurrent = useCallback((result: unknown): ResolvedTable | null => {
    if (!spec) return null
    if (isRecord) {
      if (recordFormIds.length > 0 && !catalogsQ.isSuccess) return null
      return resolveRecordTable(spec, catalogs, titles, result as never, { formatField: formatCellValue })
    }
    if (!singleCatalog) return null
    return resolveTable(spec, singleCatalog, result as never, { formatField: formatCellValue })
  }, [spec, isRecord, recordFormIds.length, catalogsQ.isSuccess, catalogs, titles, singleCatalog])

  const table = useMemo(
    () => (dataQ.data ? resolveCurrent(dataQ.data) : null),
    [dataQ.data, resolveCurrent],
  )

  const layout: 'long' | 'grouped' | 'record' =
    isRecord ? 'record' : spec?.instanceStrategy.mode === 'long' ? 'long' : 'grouped'
  const sortMap = useMemo(() => (table ? buildSortMap(table.columns, layout) : {}), [table, layout])
  const total = dataQ.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const createMut = useCreateDataViewMutation(projectId!)
  const deleteMut = useDeleteDataViewMutation(projectId!)

  const patchSpec = (next: DataViewSpec) => { setSpec(next); setPage(0); setAllRows(null) }

  // Reset the search box when switching view source.
  useEffect(() => { setSearchInput('') }, [sourceKey])

  // Apply debounced search into the (ephemeral) spec → re-runs the query.
  useEffect(() => {
    if (!spec) return
    if ((spec.search ?? '') === debouncedSearch) return
    setSpec({ ...spec, search: debouncedSearch || undefined }); setPage(0); setAllRows(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const onSort = (sortKey: string) => {
    if (!spec) return
    const dir = spec.sort?.key === sortKey && spec.sort.dir === 'asc' ? 'desc' : 'asc'
    patchSpec({ ...spec, sort: { key: sortKey, dir } })
  }

  // Fetch ALL matching rows (group-by / charts / export).
  const ensureAll = useCallback(async (): Promise<ResolvedRow[]> => {
    if (!spec || !projectId) return []
    const key = JSON.stringify(spec)
    if (allKey.current === key && allRows) return allRows
    setAllLoading(true)
    try {
      const acc: ResolvedRow[] = []
      let p = 0, fetched = 0, totalAll = Infinity
      while (fetched < totalAll && fetched < ALL_CAP) {
        const res = isRecord
          ? await queryRecordView(spec, projectId, p, ALL_PAGE)
          : await queryFormView(spec, p, ALL_PAGE)
        totalAll = res.total
        const t = resolveCurrent(res)
        if (!t || t.rows.length === 0) break
        acc.push(...t.rows)
        fetched += t.rows.length
        p++
      }
      setAllRows(acc); allKey.current = key
      return acc
    } finally {
      setAllLoading(false)
    }
  }, [spec, projectId, isRecord, allRows, resolveCurrent])

  // Load all when grouping turns on.
  useEffect(() => { if (groupColId) void ensureAll() }, [groupColId, ensureAll])

  const grouped = useMemo(
    () => (groupColId && table && allRows ? groupRows(table.columns, allRows, groupColId) : null),
    [groupColId, table, allRows],
  )

  const labelOf = useCallback((col: ViewColumn): string => {
    if (col.source === 'record_meta') return META_COLUMNS.find((m) => m.key === col.key)?.label ?? col.key
    const cat = isRecord ? catalogs[col.formId ?? ''] : singleCatalog
    return cat?.byKey[col.key]?.label ?? cat?.calculatedByKey[col.key]?.label.fa ?? col.key
  }, [isRecord, catalogs, singleCatalog])

  const onSave = async (p: SaveViewPayload) => {
    if (!spec || !projectId || !user) return
    try {
      const { id } = await createMut.mutateAsync({
        projectId, ownerId: user.id, name: p.name, description: p.description,
        visibility: p.visibility, spec, isDefault: p.isDefault,
      })
      toast({ title: 'نما ذخیره شد' }); setSaveOpen(false); setActive({ type: 'view', viewId: id })
    } catch (e) {
      toast({ title: 'خطا در ذخیرهٔ نما', description: (e as Error).message, variant: 'destructive' })
    }
  }

  // Create a brand-new saved view from the modal builder (spec built in the dialog).
  const onCreateView = async (p: ViewBuilderPayload) => {
    if (!projectId || !user) return
    try {
      const { id } = await createMut.mutateAsync({
        projectId, ownerId: user.id, name: p.name, description: p.description,
        visibility: p.visibility, spec: p.spec, isDefault: p.isDefault,
      })
      toast({ title: 'نما ساخته شد' }); setBuilderOpen(false); setActive({ type: 'view', viewId: id })
    } catch (e) {
      toast({ title: 'خطا در ساخت نما', description: (e as Error).message, variant: 'destructive' })
    }
  }

  const onDeleteView = async (v: DataViewRow) => {
    if (!user) return
    try {
      await deleteMut.mutateAsync({ id: v.id, userId: user.id })
      toast({ title: 'نما حذف شد' })
      if (active?.type === 'view' && active.viewId === v.id) setActive(null)
    } catch (e) {
      toast({ title: 'خطا در حذف', description: (e as Error).message, variant: 'destructive' })
    }
  }

  const onExport = async (opts: ExportOptions) => {
    if (!spec || !table) return
    setExporting(true)
    try {
      const rows = await ensureAll()
      const specForHeaders = { ...spec, headerStrategy: opts.headerStrategy, formPrefix: opts.formPrefix }
      // Re-resolve columns with the chosen prefix (record views only differ by prefix).
      const cols = (isRecord
        ? resolveRecordTable(specForHeaders, catalogs, titles, { total: rows.length, rows: [] }, { formatField: formatCellValue })
        : resolveTable(specForHeaders, singleCatalog!, { layout: 'long', total: 0, rows: [] }, { formatField: formatCellValue })
      ).columns
      const { headers, matrix } = tableToMatrix(cols, rows, opts.headerStrategy)
      const name = (active?.type === 'view' ? activeView?.name : headerTitle) ?? 'export'
      if (opts.format === 'csv') {
        downloadCsv(`${name}.csv`, headers, matrix)
      } else {
        await downloadXlsx(`${name}.xlsx`, { headers, matrix }, {
          viewName: name, total: rows.length, generatedAt: new Date().toISOString(),
          filters: spec.filters.map((f) => `${f.key} ${f.op} ${f.value ?? ''}`),
        })
      }
      toast({ title: 'خروجی آماده شد' }); setExportOpen(false)
    } catch (e) {
      toast({ title: 'خطا در خروجی', description: (e as Error).message, variant: 'destructive' })
    } finally {
      setExporting(false)
    }
  }

  const openChart = () => { setChartOpen(true); void ensureAll() }

  const onOpenArray = (column: ResolvedColumn, row: ResolvedRow) => {
    const entries = row.arrays[column.id] ?? []
    setArrayDialog({ title: column.label, field: column.catalogField, entries })
  }

  const isRepeatableSingle = !isRecord && catalogQ.data?.instanceType === 'repeatable'
  const headerTitle = active?.type === 'view' ? (activeView?.name ?? 'نما')
    : (catalogQ.data?.formTitle ?? 'کاوش داده‌ها')

  const groupableCols = table?.columns.filter((c) => c.kind !== 'derived') ?? []

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="کاوش داده‌ها"
        subtitle="مرور داده‌های ثبت‌شده به‌صورت جدول"
        actions={
          <Button
            variant={aiOpen ? 'default' : 'outline'}
            size="sm"
            className="gap-1.5"
            onClick={() => setAiOpen((v) => !v)}
          >
            <Sparkles className="w-3.5 h-3.5" />دستیار هوش مصنوعی
          </Button>
        }
      />
      <div className="flex flex-1 overflow-hidden border-t border-border">
        {sidebarCollapsed ? (
          <div className="shrink-0 w-9 flex flex-col items-center border-e border-border bg-card py-2">
            <button
              onClick={toggleSidebar}
              title="باز کردن پنل فرم‌ها"
              aria-label="باز کردن پنل فرم‌ها"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <PanelRightOpen className="w-4 h-4" />
            </button>
          </div>
        ) : (
        <>
        <div className="shrink-0 flex flex-col border-e border-border bg-card relative" style={{ width: sidebarW }}>
          <div className="m-2 flex items-center gap-1.5">
            <button
              onClick={() => setBuilderOpen(true)}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />ساخت نما
            </button>
            <button
              onClick={toggleSidebar}
              title="جمع کردن پنل فرم‌ها"
              aria-label="جمع کردن پنل فرم‌ها"
              className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <PanelRightClose className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <ViewSidebar
              groups={formsQ.data ?? []}
              views={views}
              aiViews={aiViewsQ.data ?? []}
              currentUserId={user?.id}
              active={active}
              onSelectForm={(fid) => setActive({ type: 'form', formId: fid })}
              onSelectView={(v) => setActive({ type: 'view', viewId: v.id })}
              onDeleteView={onDeleteView}
              onSelectAiView={(v) => { setActive({ type: 'ai', viewId: v.id }); setAiOpen(false) }}
              onDeleteAiView={(v) => { deleteAiViewMut.mutate(v.id); if (active?.type === 'ai' && active.viewId === v.id) setActive(null) }}
            />
          </div>
        </div>

        {/* draggable splitter between forms panel and data viewer */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="تغییر عرض پنل فرم‌ها"
          onPointerDown={(e) => {
            e.preventDefault()
            e.currentTarget.setPointerCapture(e.pointerId)
            const rtl = getComputedStyle(document.documentElement).direction === 'rtl'
            sidebarDrag.current = { startX: e.clientX, startW: sidebarW, rtl }
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
          }}
          onDoubleClick={() => { setSidebarW(320); try { window.localStorage.setItem('explorer-sidebar-w', '320') } catch { /* ignore */ } }}
          title="کشیدن برای تغییر عرض — دوبار کلیک برای بازنشانی"
          className="group relative shrink-0 w-1.5 cursor-col-resize bg-border hover:bg-primary/40 active:bg-primary/60 transition-colors touch-none"
        >
          {/* grip dots — appear on hover */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex flex-col gap-0.5">
              <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground" />
              <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground" />
              <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground" />
            </div>
          </div>
        </div>
        </>
        )}

        <div className={cn(
          'flex-1 flex flex-col overflow-hidden',
          fullscreen && 'fixed inset-0 z-40 bg-background',
        )}>
          {aiOpen ? (
            <Suspense fallback={<div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">در حال بارگذاری دستیار…</div>}>
              <AiWorkspace projectId={projectId} />
            </Suspense>
          ) : active?.type === 'ai' && activeAiView ? (
            <Suspense fallback={<div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">در حال بارگذاری…</div>}>
              <AiViewSurface projectId={projectId} view={activeAiView} />
            </Suspense>
          ) : !spec || !ready ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={Database}
                title={formsQ.isLoading || catalogQ.isLoading ? 'در حال بارگذاری…' : 'یک فرم یا نما را انتخاب کنید'}
                description={(formsQ.data?.length ?? 0) === 0 && !formsQ.isLoading ? 'هنوز فرم منتشرشده‌ای در این پروژه وجود ندارد.' : undefined}
              />
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold truncate">{headerTitle}</span>
                  {active?.type !== 'view' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0" title="این نما ذخیره نشده؛ زنده اجرا می‌شود">موقت</span>
                  )}
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">{total} مورد</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {isRepeatableSingle && (
                    <InstanceStrategyPicker value={spec.instanceStrategy} onChange={(s) => patchSpec({ ...spec, instanceStrategy: s })} />
                  )}
                  {isRecord ? (
                    <RecordColumnPicker
                      groups={formsQ.data ?? []}
                      columns={spec.columns}
                      formStrategies={spec.formStrategies ?? {}}
                      onColumnsChange={(cols) => patchSpec({ ...spec, columns: cols })}
                      onStrategyChange={(formId, s) => patchSpec({ ...spec, formStrategies: { ...spec.formStrategies, [formId]: s } })}
                    />
                  ) : singleCatalog && (
                    <ColumnPicker catalog={singleCatalog} formId={singleCatalog.formId} columns={spec.columns} onChange={(cols) => patchSpec({ ...spec, columns: cols })} />
                  )}
                  <ColumnReorder columns={spec.columns.filter((c) => !c.hidden)} labelOf={labelOf} onChange={(cols) => patchSpec({ ...spec, columns: cols })} />

                  {/* group-by */}
                  <Select value={groupColId || '__none__'} onValueChange={(v) => setGroupColId(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="گروه‌بندی" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">بدون گروه‌بندی</SelectItem>
                      {groupableCols.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Button variant="outline" size="sm" className="gap-1.5" onClick={openChart}><BarChart3 className="w-3.5 h-3.5" />نمودار</Button>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setExportOpen(true)}><Download className="w-3.5 h-3.5" />خروجی</Button>
                  {canManage && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <Checkbox checked={!!spec.includeDeleted} onCheckedChange={(v) => patchSpec({ ...spec, includeDeleted: !!v })} />حذف‌شده‌ها
                    </label>
                  )}
                  <Button size="sm" className="gap-1.5" onClick={() => setSaveOpen(true)}><Save className="w-3.5 h-3.5" />ذخیره</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setFullscreen((v) => !v)}
                    title={fullscreen ? 'خروج از تمام‌صفحه (Esc)' : 'نمایش تمام‌صفحه'}
                  >
                    {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    {fullscreen ? 'خروج' : 'تمام‌صفحه'}
                  </Button>
                </div>
              </div>

              {/* Search + filters */}
              <div className="px-4 py-2 border-b border-border flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search className="absolute inset-inline-start-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="جستجو در همهٔ ستون‌ها…"
                    className="h-8 w-60 ps-8"
                  />
                </div>
                {isRecord ? (
                  <RecordFilterBar catalogs={catalogs} titles={titles} filters={spec.filters} onChange={(f) => patchSpec({ ...spec, filters: f })} />
                ) : singleCatalog && (
                  <FilterBar catalog={singleCatalog} filters={spec.filters} onChange={(f) => patchSpec({ ...spec, filters: f })} />
                )}
              </div>

              {/* Grid or grouped view */}
              {groupColId && grouped ? (
                <GroupedTable columns={table!.columns} grouped={grouped} groupColId={groupColId} />
              ) : groupColId && allLoading ? (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">در حال گروه‌بندی همهٔ داده‌ها…</div>
              ) : table ? (
                <ExplorerGrid
                  columns={table.columns}
                  rows={table.rows}
                  loading={dataQ.isLoading || dataQ.isFetching}
                  sort={spec.sort}
                  sortMap={sortMap}
                  onSort={onSort}
                  onOpenArray={onOpenArray}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <EmptyState icon={Layers} title="ستونی انتخاب نشده" description="از «ستون‌ها» فیلدهای موردنظر را اضافه کنید." />
                </div>
              )}

              {/* Pagination (hidden while grouping) */}
              {!groupColId && total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-border text-sm">
                  <span className="text-muted-foreground tabular-nums">صفحهٔ {page + 1} از {pageCount}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>قبلی</Button>
                    <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>بعدی</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ViewBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        groups={formsQ.data ?? []}
        canManage={canManage}
        saving={createMut.isPending}
        onCreate={onCreateView}
      />
      {spec && (
        <SaveViewDialog open={saveOpen} onOpenChange={setSaveOpen} defaultName={headerTitle} canManage={canManage} saving={createMut.isPending} onSave={onSave} />
      )}
      {spec && table && (
        <ExportDialog open={exportOpen} onOpenChange={setExportOpen} total={total} exporting={exporting || allLoading} onExport={onExport} />
      )}
      {spec && table && chartOpen && (
        <Suspense fallback={null}>
          <ChartBuilderDialog open onOpenChange={setChartOpen} columns={table.columns} rows={allRows ?? table.rows} loadingAll={allLoading && !allRows} config={chartConfig} onConfigChange={setChartConfig} />
        </Suspense>
      )}
      <ArrayDetailDialog
        open={!!arrayDialog}
        onOpenChange={(o) => !o && setArrayDialog(null)}
        title={arrayDialog?.title ?? ''}
        field={arrayDialog?.field}
        entries={arrayDialog?.entries ?? []}
      />
    </div>
  )
}
